import { useEffect, useState } from "react";
import { Box, Breadcrumbs, Grid, Typography } from "@mui/material";
import { Assessment, Home as HomeIcon } from "@mui/icons-material";
import LinkNavegacao from "../../../common/Link";
import PageTitle from "../../../common/PageTitle";
import { listaEmpresas } from "../../management/companies/configs/functions";
import { hasPermission } from "../../../../configs/functions";
import { COLORS } from "../../dashboard/epis-dashboard/configs/config";
import { TableContracts } from "./components/TableContracts";
import { DashboardFilters } from "../../dashboard/epis-dashboard/components/DashboardFilters";
import { EpiCard } from "../../dashboard/epis-dashboard/components/DashboardCards";
import RetiradaExtragrafico from "./components/RetiradaExtraGrafico";
import Legenda from "./components/Legenda";
import SectionTitle from "../../../common/SectionTitle";
import CardAtivoInativo from "./components/CardAtivoInativo";
import Card from "./components/Card";
import CardEquipamentosPorTipo from "./components/CardEquipamentoPorTipo";
import StackedBar from "./components/StackedBar";
import { fetchDadosDashboard, getDadosSempher } from "./config/functions";
import { useCommonItems } from "../../../../contexts/CommonItensProvider";
import GraficoRetiradas from "./components/GraficoRetiradas";

const fmt = (n) => (n ?? 0).toLocaleString("pt-BR");

const TIPOS_EQUIPAMENTO = {
    maquina: "Vending Machine",
    balcao: "Balcão/PDV",
    virtual: "Balcão Web",
    armario: "Armário"
};

const mapDashboard = (res) => {
    if (!res) return null;

    const porStatus = Object.fromEntries(
        (res.retiradasExtras?.items ?? []).map((i) => [i.status, i.total])
    );

    return {
        colaboradores: res.colaboradores,
        administradores: res.administradores,
        produtos: res.produtos,
        retiradas:{
        total: res.retiradas.total,
        porMes: res.retiradas.porMes,
        ultimaRetirada: res.retiradas.ultimaRetirada,
        },
        ghe: res.ghe,
        retiradasExtras: {
            total: res.retiradasExtras?.total ?? 0,
            porStatus,
        },
        equipamentos: {
            ativos: res.equipamentos?.ativos ?? 0,
            inativos: res.equipamentos?.inativos ?? 0,
            total: res.equipamentos?.total ?? 0,
            porTipo: (res.equipamentos?.items ?? []).map((i) => ({
                ...i,
                tipo: TIPOS_EQUIPAMENTO[i.tipo] ?? i.tipo ?? 'armario',
            })),
        },
    };
};

export function InternalDashboard() {
    const { exibirAlerta } = useCommonItems();

    const [error, setError] = useState(null);

    const [empresas, setEmpresas] = useState([]);
    const [empresa, setEmpresa] = useState([1]);
    const [period, setPeriod] = useState(30);
    const [year, setYear] = useState(new Date().getFullYear());

    const [geral, setGeral] = useState(null);
    const [dadosEmpresa, setDadosEmpresa] = useState(null);
    const [loadingGeral, setLoadingGeral] = useState(false);
    const [loadingEmpresa, setLoadingEmpresa] = useState(false);

    useEffect(() => {
        if (hasPermission(["admin", "admin_empresas", "list_empresas"])) {
            listaEmpresas(null, null, null, true)
                .then((response) => {
                    const raw = response.data.data;
                    setEmpresas(Array.isArray(raw) ? raw : [raw]);
                })
                .catch((e) => setError(e.message));
        }
    }, []);

    useEffect(() => {
        let ativo = true;
        setLoadingGeral(true);

        (async () => {
            try {
                const res = await getDadosSempher();
                if (!ativo) return;
                setGeral(mapDashboard(res));
            } catch (e) {
                if (!ativo) return;
                exibirAlerta("Erro", "Erro ao carregar visão geral", "error");
                setGeral(null);
            } finally {
                if (ativo) setLoadingGeral(false);
            }
        })();

        return () => { ativo = false; };
    }, []);

    useEffect(() => {
        let ativo = true;
        setLoadingEmpresa(true);
        (async () => {
            try {
                const res = await fetchDadosDashboard(empresa, period, year);
                if (!ativo) return;
                setDadosEmpresa(mapDashboard(res));
            } catch (e) {
                console.error(e);
                if (!ativo) return;
                exibirAlerta("Erro", "Erro ao carregar dados da empresa", "error");
                setDadosEmpresa(null);
            } finally {
                if (ativo) setLoadingEmpresa(false);
            }
        })();

        return () => { ativo = false; };
    }, [empresa, period, year]);

    const kpisGeral = [
        {
            label: "Total de Retiradas",
            valor: fmt(geral?.retiradas?.total),
            badge: "Total",
            badgeColor: COLORS.g1,
            icon: "🏃",
            frase: "Todas as empresas",

        },
        {
            label: "Total de Retiradas extras",
            valor: fmt(geral?.retiradasExtras?.total),
            badge: "Total",
            badgeColor: COLORS.g1,
            badgeBg: COLORS.kpiBg,
            icon: "📋",
            frase: "Todas as empresas",
        },
        {
            label: "Equipamentos ativos",
            valor: fmt(geral?.equipamentos?.ativos),
            badge: "Sistema",
            badgeColor: COLORS.g1,
            badgeBg: COLORS.kpiBg,
            icon: "📦",
            frase: `${fmt(geral?.equipamentos?.inativos)} inativos`,
        },
        {
            label: "GHE",
            valor: fmt(geral?.ghe?.total),
            badge: "Sistema",
            badgeColor: COLORS.g1,
            badgeBg: COLORS.kpiBg,
            icon: "🦺",
            frase: "Grupos homogêneos de exposição",
        },
    ];

    const colabEmpresa = dadosEmpresa?.colaboradores;
    const eq = dadosEmpresa?.equipamentos;
    const prod = dadosEmpresa?.produtos;
    const adminEmpresa = dadosEmpresa?.administradores;
    const retiradasRegra = dadosEmpresa?.retiradas;

    const kpisEmpresa = [
        {
            label: "Total de Retiradas extras",
            valor: fmt(dadosEmpresa?.retiradasExtras?.total),
            badge: `${period}d`,
            badgeColor: COLORS.g1,
            badgeBg: COLORS.kpiBg,
            icon: "📋",
            frase: "Solicitadas no período",
        },
        {
            label: "Produtos ativos",
            valor: fmt(prod?.ativos),
            badge: "estático",
            badgeColor: COLORS.g1,
            badgeBg: COLORS.kpiBg,
            icon: "📦",
            frase: `${fmt(prod?.inativos)} inativos`,
        },
        {
            label: "GHE",
            valor: fmt(dadosEmpresa?.ghe?.total),
            badge: "estático",
            badgeColor: COLORS.g1,
            badgeBg: COLORS.kpiBg,
            icon: "🦺",
            frase: "Grupos homogêneos de exposição",
        },
    ];

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, minHeight: "100vh" }}>
            <Grid item xs={12} mb={2}>
                <Breadcrumbs>
                    <LinkNavegacao to="/"><HomeIcon fontSize="small" /></LinkNavegacao>
                    <LinkNavegacao to="/epis">Gerenciamento admin</LinkNavegacao>
                    <Typography variant="span">Dashboard Interna</Typography>
                </Breadcrumbs>
                <PageTitle icon={<Assessment fontSize="large" />} title="Dashboard" />
            </Grid>

            {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

            <SectionTitle title="Visão geral do sistema" subtitle="Dados consolidados de todas as empresas" />

            <Grid container spacing={1.5} mb={1.5}>
            {kpisGeral.map((k, i) => (
                <Grid key={i} item xs={12} sm={3} md={3}> 
                <EpiCard {...k} />
                </Grid>
            ))}
            </Grid>

            <Grid container spacing={1.5} mb={1.5}>
                <CardAtivoInativo
                    title="Colaboradores"
                    subtitle="Ativos e inativos no sistema"
                    ativos={geral?.colaboradores?.ativos}
                    inativos={geral?.colaboradores?.inativos}
                    loading={loadingGeral}
                />
                <CardAtivoInativo
                    title="Administradores"
                    subtitle="Ativos e inativos no sistema"
                    ativos={geral?.administradores?.ativos}
                    inativos={geral?.administradores?.inativos}
                    loading={loadingGeral}
                />
                <CardAtivoInativo
                    title="Produtos"
                    subtitle="Ativos e inativos no sistema"
                    ativos={geral?.produtos?.ativos}
                    inativos={geral?.produtos?.inativos}
                    loading={loadingGeral}
                />
            </Grid>

            <Grid container spacing={1.5} mb={1.5}>
                <Grid item xs={12} md={4}>
                    <CardEquipamentosPorTipo tipos={geral?.equipamentos?.porTipo} loading={loadingGeral} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <RetiradaExtragrafico data={geral?.retiradasExtras?.porStatus ?? {}} todos = {geral?.retiradasExtras.total} loading={loadingGeral} />
                </Grid>
            </Grid>

            <SectionTitle
                title="Por empresa"
                subtitle="Selecione uma empresa. O período filtra apenas as retiradas, os demais dados são fixos."
            />

            <DashboardFilters
                empresas={empresas}
                empresa={empresa}
                setEmpresa={setEmpresa}
                period={period}
                setPeriod={setPeriod}
                year={year}
                setYear={setYear}
                loading={loadingEmpresa}
                todos = {true}
            />

            <Grid container spacing={1.5} mb={1.5}>
                {kpisEmpresa.map((k, i) => (
                    <Grid key={i} item xs={12} sm={4} md={4}>
                        <EpiCard {...k} />
                    </Grid>
                ))}
            </Grid>
            <Grid container spacing={1.5} mb={1.5}>
                <Grid item xs={12} md={4}>
                    <RetiradaExtragrafico data={dadosEmpresa?.retiradasExtras?.porStatus ?? {}} todos = {dadosEmpresa?.retiradasExtras?.total} loading={loadingEmpresa} />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card title="Colaboradores" subtitle="Cadastrados na empresa" loading={loadingEmpresa}>
                        <Typography sx={{ fontSize: 28, fontWeight: 700, color: COLORS.tPri }}>
                            {fmt(colabEmpresa?.total)}
                        </Typography>
                        <Box sx={{ my: 1.25 }}>
                            <StackedBar
                                total={colabEmpresa?.total}
                                partes={[
                                    { label: "Ativos", valor: colabEmpresa?.ativos ?? 0, cor: COLORS.g1 },
                                    { label: "Inativos", valor: colabEmpresa?.inativos ?? 0, cor: COLORS.g4 },
                                ]}
                            />
                        </Box>
                        <Legenda
                            itens={[
                                { label: "Ativos", valor: colabEmpresa?.ativos, cor: COLORS.g1 },
                                { label: "Inativos", valor: colabEmpresa?.inativos, cor: COLORS.g4 },
                            ]}
                        />
                    </Card>
                </Grid>

                <CardAtivoInativo
                    title="Administradores"
                    subtitle="Ativos e inativos da empresa"
                    ativos={adminEmpresa?.ativos}
                    inativos={adminEmpresa?.inativos}
                    loading={loadingEmpresa}
                />
            </Grid>

            <Grid container spacing={1.5} mb={1.5}>
                <CardAtivoInativo
                    title="Equipamentos"
                    subtitle="Ativos e inativos da empresa"
                    ativos={eq?.ativos}
                    inativos={eq?.inativos}
                    loading={loadingEmpresa}
                />
                <CardAtivoInativo
                    title="Produtos"
                    subtitle="Ativos e inativos da empresa"
                    ativos={prod?.ativos}
                    inativos={prod?.inativos}
                    loading={loadingEmpresa}
                />

               <Grid item xs={12} md={4}>
                <GraficoRetiradas total={retiradasRegra?.total} data={retiradasRegra?.porMes ?? []} ultima={retiradasRegra?.ultimaRetirada} loading={loadingEmpresa} />
                </Grid>
            </Grid>
          <TableContracts /> 
        </Box>
    );
}

export default InternalDashboard;